import { useEffect, useRef, type ReactNode } from 'react';
import './sheet.css';

// Modal bottom sheet built on native <dialog> (focus trapping + inert backdrop
// for free). Slides up from the bottom; dismiss via backdrop tap or Escape.
export function BottomSheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    else if (!open && dlg.open) dlg.close();
  }, [open]);

  // Backdrop click = clicks that land on the dialog element itself.
  const onClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === ref.current) onClose();
  };

  return (
    <dialog ref={ref} className="sheet" onClose={onClose} onClick={onClick}>
      <div className="sheet-panel" role="document">
        <div className="sheet-grabber" aria-hidden="true" />
        {title ? <h2 className="sheet-title">{title}</h2> : null}
        {children}
      </div>
    </dialog>
  );
}

export interface SheetAction {
  label: string;
  onClick: () => void;
  danger?: boolean;
  icon?: ReactNode;
}

export function ActionSheet({
  open,
  onClose,
  title,
  actions,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  actions: SheetAction[];
}) {
  return (
    <BottomSheet open={open} onClose={onClose} title={title}>
      <div className="sheet-actions">
        {actions.map((a) => (
          <button
            key={a.label}
            className={`sheet-action${a.danger ? ' danger' : ''}`}
            onClick={() => {
              onClose();
              a.onClick();
            }}
          >
            {a.icon}
            {a.label}
          </button>
        ))}
        <button className="sheet-action sheet-cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
    </BottomSheet>
  );
}
